import moment from 'moment';
import firebase from '../config/firebase.config';
import map from 'lodash/map';
import { reject } from 'lodash';
import groupBy from 'lodash/groupBy';
import { getTeamMemberDetails } from './teamMemberDetails';
import {
  readShardSnapshotsInTransaction,
  resolveMemberRosterLocation,
  buildRosterPatchWrite,
  loadTeamAndShardSnapshots,
} from './teamMemberDetailsRosterPatch';
import httpClient from '@/config/httpClient';
const database = firebase.firestore();

/** Firestore batch limit */
const BATCH_WRITE_LIMIT = 500;

/**
 * Build the teamList document payload for an Admin (same shape as createTeamWithMembers).
 * @param {string} teamName
 * @param {string} teamId
 * @param {string} email
 * @returns {{ Role: string, Team: string, activeTeamid: string, email: string }}
 */
function buildTeamListDocForAdmin(teamName, teamId, email) {
  return {
    Role: 'Admin',
    Team: teamName,
    activeTeamid: teamId,
    email,
  };
}

/**
 * Build the memberDetails entry for an Admin (same shape as createTeamWithMembers).
 * @param {string} email
 * @param {string} name
 * @returns {{ userEmailid: string, userName: string, userType: string }}
 */
function buildMemberDetailForAdmin(email, name) {
  return {
    userEmailid: email,
    userName: name,
    userType: 'Admin',
  };
}

/**
 * Add one user to one team as Admin (batch only). Same DB shape as createTeamWithMembers admin block.
 * @param {FirebaseFirestore.WriteBatch} batch
 * @param {string} userId
 * @param {string} companyId
 * @param {string} teamId
 * @param {string} teamName
 * @param {string} email
 * @param {string} name
 */
function addUserToTeamAsAdmin(
  batch,
  userId,
  companyId,
  teamId,
  teamName,
  email,
  name
) {
  const teamRef = database.collection('teamDatabase').doc(teamId);
  const memberTeamListRef = database
    .collection('membersDatabase')
    .doc(userId)
    .collection('companyList')
    .doc(companyId)
    .collection('teamList')
    .doc(teamId);

  batch.update(teamRef, {
    [`memberDetails.${userId}`]: buildMemberDetailForAdmin(email, name),
  });
  batch.set(
    memberTeamListRef,
    buildTeamListDocForAdmin(teamName, teamId, email),
    { merge: true }
  );
}

/**
 * Add a user (new Admin) to all teams of a company and to company admins array.
 * Used when promoting someone to Admin so they get access to every team (same as admins at team creation).
 * @param {string} userId
 * @param {string} companyId
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function addAdminToAllCompanyTeams(userId, companyId) {
  try {
    const companyRef = database.collection('companyDatabase').doc(companyId);
    const companySnap = await companyRef.get();
    if (!companySnap.exists) {
      return { success: false, message: 'Company not found' };
    }
    const companyData = companySnap.data();
    const teamIds = companyData.teams || [];
    if (teamIds.length === 0) {
      const admins = companyData.admins || [];
      if (!admins.includes(userId)) {
        await companyRef.update({
          admins: firebase.firestore.FieldValue.arrayUnion(userId),
        });
      }
      return { success: true };
    }

    const memberSnap = await database
      .collection('membersDatabase')
      .doc(userId)
      .get();
    if (!memberSnap.exists) {
      return { success: false, message: 'User not found in membersDatabase' };
    }
    const memberData = memberSnap.data();
    const email = memberData.accountEmailid || '';
    const name = memberData.accountFullName || '';

    const teamSnaps = await Promise.all(
      teamIds.map((tid) => database.collection('teamDatabase').doc(tid).get())
    );
    const teamIdToName = {};
    teamSnaps.forEach((snap, i) => {
      if (snap.exists && teamIds[i]) {
        teamIdToName[teamIds[i]] = snap.data().teamName || '';
      }
    });

    let batch = database.batch();
    let opCount = 0;

    const admins = companyData.admins || [];
    if (!admins.includes(userId)) {
      batch.update(companyRef, {
        admins: firebase.firestore.FieldValue.arrayUnion(userId),
      });
      opCount += 1;
    }

    for (const teamId of teamIds) {
      const teamName = teamIdToName[teamId];
      if (teamName == null) continue;
      addUserToTeamAsAdmin(
        batch,
        userId,
        companyId,
        teamId,
        teamName,
        email,
        name
      );
      opCount += 2;
      if (opCount >= BATCH_WRITE_LIMIT) {
        await batch.commit();
        batch = database.batch();
        opCount = 0;
      }
    }
    if (opCount > 0) {
      await batch.commit();
    }
    return { success: true };
  } catch (e) {
    console.error('[addAdminToAllCompanyTeams] error', e);
    return {
      success: false,
      message: e.message || 'Failed to add admin to all company teams',
    };
  }
}

/**
 * Mirror of addAdminToAllCompanyTeams: when demoting from company Admin to Member/Manager,
 * remove uid from company admins and set userType + teamList Role on every team where the user exists.
 * @param {string} userId
 * @param {string} companyId
 * @param {'Member'|'Manager'} newRole
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function demoteCompanyAdminToRole(userId, companyId, newRole) {
  if (newRole !== 'Member' && newRole !== 'Manager') {
    return { success: true };
  }
  try {
    const companyRef = database.collection('companyDatabase').doc(companyId);
    const companySnap = await companyRef.get();
    if (!companySnap.exists) {
      return { success: false, message: 'Company not found' };
    }
    const companyData = companySnap.data();
    const teamIds = companyData.teams || [];

    let batch = database.batch();
    let opCount = 0;

    const admins = companyData.admins || [];
    if (admins.includes(userId)) {
      batch.update(companyRef, {
        admins: firebase.firestore.FieldValue.arrayRemove(userId),
      });
      opCount += 1;
    }

    const memberRef = database.collection('membersDatabase').doc(userId);
    const companyListRef = memberRef.collection('companyList').doc(companyId);

    if (teamIds.length > 0) {
      for (const tid of teamIds) {
        const { teamRef, teamSnap, shardSnapshots } =
          await loadTeamAndShardSnapshots(database, tid);
        if (!teamSnap.exists) continue;
        const loc = resolveMemberRosterLocation(
          teamSnap.data() || {},
          shardSnapshots,
          userId
        );
        if (loc.type === 'none') continue;

        const teamListRef = companyListRef.collection('teamList').doc(tid);
        let rosterWrite;
        try {
          rosterWrite = buildRosterPatchWrite(teamRef, loc, userId, {
            userType: newRole,
          });
        } catch (e) {
          console.error('[demoteCompanyAdminToRole] roster patch', tid, e);
          continue;
        }

        batch.update(rosterWrite.ref, rosterWrite.data);
        batch.set(teamListRef, { Role: newRole }, { merge: true });
        opCount += 2;
        if (opCount >= BATCH_WRITE_LIMIT) {
          await batch.commit();
          batch = database.batch();
          opCount = 0;
        }
      }
    }

    if (opCount > 0) {
      await batch.commit();
    }
    return { success: true };
  } catch (e) {
    console.error('[demoteCompanyAdminToRole] error', e);
    return {
      success: false,
      message: e.message || 'Failed to demote admin across company',
    };
  }
}

/**
 * Gets all team members from all teams
 * @param {*} teams
 * @returns
 */
async function getAllTeamMembersData(payload) {
  let { teams, filterBy } = payload;
  try {
    let tempTeams = [...teams];
    let finalizeTeamMembers = [];

    let count = 0;
    do {
      let teamId = tempTeams[count].activeTeamid;
      let teamName = tempTeams[count].Team;
      const memberDetails = await getTeamMemberDetails(database, teamId);
      const data = Object.entries(memberDetails)
        .filter(([, m]) => m && m.deleted !== true)
        .map(([userUID, value]) => ({
          ...value,
          userUID,
          role: value.userType,
        }));
      if (data.length > 0) {
        map(data, (member) => {
          finalizeTeamMembers.push({ teamId, teamName, ...member });
        });
      }
      count = count + 1;
    } while (count < tempTeams.length);

    let groupedData = groupBy(finalizeTeamMembers, 'userEmailid');

    let allMembers = [];

    allMembers = map(groupedData, (group, index) => {
      let obj = {};
      obj['userUID'] = group[0].userUID;
      obj['userEmail'] = group[0].userEmailid;
      obj['userName'] = group[0].userName;
      obj['role'] = group[0].role;
      obj['rates'] = group[0].rates;
      // Helper function to deduplicate teams based on teamId
      const deduplicateTeams = (teams) => {
        const seen = new Set();
        return teams.filter((team) => {
          const duplicate = seen.has(team.teamId);
          seen.add(team.teamId);
          return !duplicate;
        });
      };

      if (!filterBy) {
        obj['teams'] = deduplicateTeams(
          group.map((fnlItm) => ({
            teamId: fnlItm.teamId,
            teamName: fnlItm.teamName,
            role: fnlItm.userType,
          }))
        );
      } else {
        let preparedTms = [];
        if (filterBy?.teams && filterBy?.role) {
          let isMember = group.filter((itm) => itm.userType == filterBy?.role);
          preparedTms = deduplicateTeams(
            isMember
              .filter((itm) => filterBy?.teams.includes(itm.teamId))
              .map((fnlItm) => ({
                teamId: fnlItm.teamId,
                teamName: fnlItm.teamName,
                role: fnlItm.userType,
              }))
          );
        } else if (filterBy?.teams) {
          preparedTms = deduplicateTeams(
            group
              .filter((itm) => filterBy?.teams.includes(itm.teamId))
              .map((fnlItm) => ({
                teamId: fnlItm.teamId,
                teamName: fnlItm.teamName,
                role: fnlItm.userType,
              }))
          );
        } else if (filterBy?.role) {
          preparedTms = deduplicateTeams(
            group
              .filter((itm) => itm.userType == filterBy?.role)
              .map((fnItm) => ({
                teamId: fnItm.teamId,
                teamName: fnItm.teamName,
                role: fnItm.userType,
              }))
          );
        }
        obj['teams'] = preparedTms;
      }
      return { ...obj };
    });
    if (filterBy) {
      let temp = allMembers.filter((fm) => fm.teams.length > 0);
      allMembers = [...temp];
    }
    return { success: true, data: [...allMembers] };
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Create new team with or without members
 * @param {*} payload
 * @returns
 */
async function createTeamWithMembers(payload) {
  try {
    const { teamName, ownedBy, createdBy, members = [] } = payload;

    const cityRef = database.collection('companyDatabase').doc(ownedBy);
    const doc = await cityRef.get();
    const companyTeam = doc.data();

    let teamDBRef = database.collection('teamDatabase').doc(),
      memberDBRef = database
        .collection('membersDatabase')
        .doc(createdBy.userId),
      memberCompanyListRef = memberDBRef
        .collection('companyList')
        .doc(ownedBy)
        .collection('teamList')
        .doc(teamDBRef.id),
      // ownerDBRef = memberDBRef.collection('teamList').doc(teamDBRef.id),
      companyTeamsRef = database
        .collection('companyDatabase')
        .doc(ownedBy)
        .collection('teams')
        .doc(teamDBRef.id);
    let companyDBRef = database.collection('companyDatabase').doc(ownedBy);

    let batch = database.batch(),
      memberDetails = {},
      membersIDs = [];
    // 1. Extract admins from the company data
    const adminUserIds = companyTeam.admins || [];

    // 2. For each admin, fetch member details and add to memberDetails + member's teamList (same shape as addUserToTeamAsAdmin)
    for (const adminId of adminUserIds) {
      const adminDoc = await database
        .collection('membersDatabase')
        .doc(adminId)
        .get();
      if (adminDoc.exists) {
        const adminData = adminDoc.data();
        const email = adminData.accountEmailid;
        const name = adminData.accountFullName;

        memberDetails[adminId] = buildMemberDetailForAdmin(email, name);
        membersIDs.push(adminId);

        const adminTeamListRef = database
          .collection('membersDatabase')
          .doc(adminId)
          .collection('companyList')
          .doc(ownedBy)
          .collection('teamList')
          .doc(teamDBRef.id);
        batch.set(
          adminTeamListRef,
          buildTeamListDocForAdmin(teamName, teamDBRef.id, email),
          { merge: true }
        );
      }
    }

    membersIDs = members.map((i) => i.userUID);

    const data = {
      teamId: teamDBRef.id,
      members: membersIDs,
    };

    // Sets a creator as a default member
    memberDetails[createdBy.userId] = {
      userEmailid: createdBy.email,
      userName: createdBy.name,
      userType: createdBy.role,
    };

    members.map((mem) => {
      memberDetails[mem.userUID] = {
        userEmailid: mem.userEmail,
        userName: mem.userName,
        userType: mem?.role,
      };

      // ** Adds team details to individual member's account relevant to company
      let memTeamListRef = database
        .collection('membersDatabase')
        .doc(mem.userUID)
        .collection('companyList')
        .doc(ownedBy)
        .collection('teamList')
        .doc(teamDBRef.id);
      batch.set(
        memTeamListRef,
        {
          Tasks: {},
          Role: mem?.role,
          Team: teamName,
          completedTasks: {},
          email: mem.userEmail,
        },
        { merge: true }
      );
    });

    // ** Adds to the teamList under the companyList in CompanyDatabase
    batch.set(
      memberCompanyListRef,
      {
        Role: createdBy.role,
        Team: teamName,
        email: createdBy.email,
        activeTeamid: teamDBRef.id,
      },
      { merge: true }
    );

    // ** Adds to teamDb with memberDetails in teamDatabase
    batch.set(
      teamDBRef,
      {
        teamName: teamName,
        ownedBy: ownedBy,
        createdBy: createdBy.userId,
        memberDetails: { ...memberDetails },
      },
      { merge: true }
    );

    // ** Updates to the companyDatabase
    batch.set(
      companyTeamsRef,
      {
        teamId: teamDBRef.id,
        // members: membersIDs.length > 0 ? membersIDs : {},
        ...(membersIDs.length > 0 ? { members: membersIDs } : {}),
      },
      { merge: true }
    );

    if (companyTeam.teams) {
      const newData = companyTeam.teams;
      newData.push(data);
      batch.update(companyDBRef, {
        teams: firebase.firestore.FieldValue.arrayUnion(teamDBRef.id),
      });
    } else {
      batch.update(companyDBRef, {
        teams: [teamDBRef.id],
      });
    }

    return batch.commit().then(() => {
      return { success: true, data: 'Team created successfully!' };
    });
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Edit team details with members
 * @param {*} payload
 * @returns
 */
async function editTeamWithMembers(payload) {
  try {
    const { teamName, teamId, createdBy, members, ownedBy, shallRemove } =
      payload;

    let teamDBRef = database.collection('teamDatabase').doc(teamId);
    // let ownerDBRef = database.collection("membersDatabase").doc(createdBy.userId).collection('teamList').doc(teamId)
    let memberCompanyListRef = database
      .collection('membersDatabase')
      .doc(createdBy.userId)
      .collection('companyList')
      .doc(ownedBy)
      .collection('teamList')
      .doc(teamId);
    let companyTeamsRef = database
      .collection('companyDatabase')
      .doc(ownedBy)
      .collection('teams')
      .doc(teamDBRef.id);
    let batch = database.batch();
    let memberDetails = {};
    let membersIDs = [];
    membersIDs = members.map((i) => i.userUID);

    // Sets a editor as a default member
    memberDetails[createdBy.userId] = {
      userEmailid: createdBy.email,
      userName: createdBy.name,
      userType: createdBy.role,
    };

    if (members.length > 0) {
      members.map((mem) => {
        memberDetails[mem.userUID] = {
          userEmailid: mem.userEmail,
          userName: mem.userName,
          userType: mem.role,
        };

        // ** If user is not the owner of the team
        if (mem.userUID != createdBy.userId) {
          // ** Updates team details to individual member's account

          let memTeamListRef = database
            .collection('membersDatabase')
            .doc(mem.userUID)
            .collection('companyList')
            .doc(ownedBy)
            .collection('teamList')
            .doc(teamId);
          batch.set(
            memTeamListRef,
            {
              Role: mem?.role,
              Team: teamName,
              email: mem.userEmail,
            },
            { merge: true }
          );

          // let memRef = database.collection("membersDatabase").doc(mem.userUID).collection('teamList').doc(teamId)
          // batch.set(memRef, {
          //     Role: mem.role,
          //     Team: teamName,
          //     email: mem.userEmail
          // }, { merge: true })
        }
      });
    }

    // ** Update to the collection of the user that created this team
    // batch.update(ownerDBRef, { Team: teamName })

    // ** Update to the collection of the user that created this team
    batch.update(memberCompanyListRef, { Team: teamName });

    // ** Updates to teamDb with memberDetails
    batch.update(teamDBRef, {
      teamName: teamName,
      memberDetails: { ...memberDetails },
    });

    // ** Updates to the companyDatabase
    if (membersIDs.length > 0) {
      batch.set(
        companyTeamsRef,
        {
          members: membersIDs,
        },
        { merge: true }
      );
    }

    if (shallRemove.length > 0) {
      shallRemove.map((srId) => {
        // ** Removes team details from individual member's account
        // let srRef = database.collection("membersDatabase").doc(srId).collection('teamList').doc(teamId)
        let srListRef = database
          .collection('membersDatabase')
          .doc(srId)
          .collection('companyList')
          .doc(ownedBy)
          .collection('teamList')
          .doc(teamId);
        batch.delete(srListRef);
        // batch.delete(srRef)
      });
    }

    return batch
      .commit()
      .then(() => {
        return { success: true, data: 'Team updated successfully!' };
      })
      .catch((e) => {
        return { success: false, data: e };
      });
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Deletes team from database
 * @param {*} payload
 * @returns
 */
async function deleteTeam(payload) {
  try {
    const { selectedTeam, ownerId, companyId } = payload;
    const { team, teamName, members } = selectedTeam;

    let teamDBRef = database.collection('teamDatabase').doc(team);
    // let ownerDBRef = database.collection("membersDatabase").doc(ownerId).collection('teamList').doc(team)
    let ownerDBListRef = database
      .collection('membersDatabase')
      .doc(ownerId)
      .collection('companyList')
      .doc(companyId)
      .collection('teamList')
      .doc(team);
    let companyTeamsRef = database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('teams')
      .doc(team);
    let batch = database.batch();

    // ** Deletes team from teamDatabase
    batch.delete(teamDBRef);
    // batch.delete(ownerDBRef)
    batch.delete(ownerDBListRef);
    batch.delete(companyTeamsRef);

    members.map((mem) => {
      // ** Deletes team details from individual member's account
      // let memRef = database.collection("membersDatabase").doc(mem.userUID).collection('teamList').doc(team)
      let memListRef = database
        .collection('membersDatabase')
        .doc(mem.userUID)
        .collection('companyList')
        .doc(companyId)
        .collection('teamList')
        .doc(team);
      batch.delete(memListRef);
      // batch.delete(memRef)
    });

    return batch.commit().then(() => {
      return { success: true, data: 'Team deleted successfully!' };
    });
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Updates individual member's details
 * @param {*} payload
 * @returns
 */
async function updateMemberDetails(payload) {
  try {
    let {
      name,
      email,
      role,
      selectedTeam,
      teamDetails,
      oldTeam,
      userUID,
      companyId,
      activeCompanyId,
      activeTeamId,
      rates,
      timeZone,
      deskId,
    } = payload;

    let batch = database.batch();

    // Declarations according to selected team (new updated team)
    const teamRef = database.collection('teamDatabase').doc(selectedTeam);
    const memberDBRef = database.collection('membersDatabase').doc(userUID);
    // const memberTeamListRef = memberDBRef.collection('teamList').doc(selectedTeam)
    const memberCompanyTeamListRef = memberDBRef
      .collection('companyList')
      .doc(companyId)
      .collection('teamList')
      .doc(selectedTeam);

    // Prepare member details object with all fields
    const memberDetailsUpdate = {
      userEmailid: email,
      userName: name,
      userType: role,
    };

    // Add optional fields if provided
    if (rates !== undefined) {
      memberDetailsUpdate.rates = rates;
    }
    if (timeZone !== undefined && timeZone !== '') {
      memberDetailsUpdate.timeZone = timeZone;
    }

    if (!oldTeam) {
      // Updates user details in teamDatabase (including rates, timeZone if provided)
      batch.update(teamRef, {
        [`memberDetails.${userUID}`]: memberDetailsUpdate,
      });
      // Updates team details in membersDB
      // batch.update(memberTeamListRef, { Role: role, email: email })
      // batch.update(memberCompanyTeamListRef,)
    } else {
      // ** Removes details from old team

      // Remove member from the old team
      let oldTeamRef = database.collection('teamDatabase').doc(oldTeam);
      batch.update(oldTeamRef, {
        [`memberDetails.${userUID}`]: firebase.firestore.FieldValue.delete(),
      });

      // Removes from old team - companyDatabase
      let oldCompanyTeamRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('teams')
        .doc(oldTeam);
      batch.update(oldCompanyTeamRef, {
        members: firebase.firestore.FieldValue.arrayRemove(userUID),
      });

      // Removes from memberDB
      // let oldMemTeamListRef = memberDBRef.collection('teamList').doc(oldTeam)
      let oldCompanyTeamListRef = memberDBRef
        .collection('companyList')
        .doc(companyId)
        .collection('teamList')
        .doc(oldTeam);
      // batch.delete(oldMemTeamListRef)
      batch.delete(oldCompanyTeamListRef);

      // ** Adds details in new team
      batch.set(
        teamRef,
        {
          memberDetails: {
            [userUID]: memberDetailsUpdate,
          },
        },
        { merge: true }
      );

      // ** Add new team to member's account inside companyList & teamList
      // batch.set(memberTeamListRef, {
      //     Role: role,
      //     Team: teamDetails?.label,
      //     email: email
      // }, { merge: true })
      batch.set(
        memberCompanyTeamListRef,
        {
          Role: role,
          Team: teamDetails?.label,
          email: email,
        },
        { merge: true }
      );

      // Adds to new team in companyDatabase
      let newCompanyTeamRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('teams')
        .doc(selectedTeam);
      batch.update(newCompanyTeamRef, {
        members: firebase.firestore.FieldValue.arrayUnion(userUID),
      });
    }

    // ** Update activeCompanyId and activeTeamId in membersDatabase base path
    if (activeCompanyId !== undefined || activeTeamId !== undefined) {
      const updateData = {};
      if (activeCompanyId !== undefined) {
        updateData.activeCompanyId = activeCompanyId;
      }
      if (activeTeamId !== undefined) {
        updateData.activeTeamId = activeTeamId;
      }
      batch.update(memberDBRef, updateData);
    }

    return batch
      .commit()
      .then(() => {
        return { success: true, data: 'Member details updated successfully' };
      })
      .catch((e) => ({ success: false, data: e }));
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Deletes user from selected company
 * @param {*} payload
 * @returns
 */
async function deleteMember(payload) {
  try {
    let { userUID, companyId, teams } = payload;

    let batch = database.batch();
    let operationCount = 0;
    const maxBatchOperations = 500;

    for (const team of teams) {
      const teamId = team.teamId;

      const teamRef = database
        .collection('membersDatabase')
        .doc(userUID)
        .collection('companyList')
        .doc(companyId)
        .collection('teamList')
        .doc(teamId);

      batch.update(teamRef, {
        deleted: true,
        deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      operationCount++;

      // const companyTeamRef = database.collection('companyDatabase')
      //     .doc(companyId)
      //     .collection('teams')
      //     .doc(teamId);

      // batch.update(companyTeamRef, {
      //     members: firebase.firestore.FieldValue.arrayRemove(userUID),
      //     [`memberDetails.${userUID}.deleted`]: true,
      //     [`memberDetails.${userUID}.deletedAt`]: firebase.firestore.FieldValue.serverTimestamp()
      // });
      // operationCount++;

      const teamDatabaseRef = database.collection('teamDatabase').doc(teamId);

      batch.update(teamDatabaseRef, {
        [`memberDetails.${userUID}.deleted`]: true,
        [`memberDetails.${userUID}.deletedAt`]:
          firebase.firestore.FieldValue.serverTimestamp(),
      });
      operationCount++;

      if (operationCount >= maxBatchOperations) {
        await batch.commit();
        batch = database.batch();
        operationCount = 0;
      }
    }

    if (operationCount > 0) {
      await batch.commit();
    }

    return { success: true, data: 'Member deleted successfully!' };
  } catch (e) {
    return { success: false, data: e };
  }
}

const VALID_ROLES = ['Member', 'Manager', 'Admin'];

/**
 * Updates employee details (partial update: only provided fields are updated).
 * - name: updates membersDatabase.accountFullName and teamDatabase memberDetails.{userId}.userName
 *   on every team in this company where the user has a non-deleted teamList entry
 * - deskId: verifies license exists, resolves conflicts, sets membersDatabase.encryptedLicenseKey
 * - timeZone: updates membersDatabase.timeZone only (not team roster)
 * - role: updates membersDatabase companyList/teamList and roster userType on the edited team (legacy or shard)
 * @param {Object} payload - { userId, companyId, teamId, name?, deskId?, timeZone?, role? }
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function updateEmployeeDetails(payload) {
  console.log('[updateEmployeeDetails] payload', payload);
  const { userId, companyId, teamId, deskId, timeZone, role, name } = payload;
  if (!userId) {
    return { success: false, message: 'userId is required' };
  }
  if (name !== undefined && name !== null && String(name).trim() === '') {
    return { success: false, message: 'Name cannot be empty' };
  }
  const hasDeskId =
    deskId !== undefined && deskId !== null && String(deskId).trim() !== '';
  const hasTimeZone =
    timeZone !== undefined &&
    timeZone !== null &&
    String(timeZone).trim() !== '';
  const hasRole =
    role !== undefined && role !== null && VALID_ROLES.includes(role);
  const hasName =
    name !== undefined && name !== null && String(name).trim() !== '';
  console.log(
    '[updateEmployeeDetails] hasDeskId',
    hasDeskId,
    'hasTimeZone',
    hasTimeZone,
    'hasRole',
    hasRole,
    'hasName',
    hasName
  );
  if (!hasDeskId && !hasTimeZone && !hasRole && !hasName) {
    return {
      success: false,
      message: 'At least one of name, deskId, timeZone, or role is required',
    };
  }
  if (hasRole && !VALID_ROLES.includes(role)) {
    return {
      success: false,
      message: `role must be one of: ${VALID_ROLES.join(', ')}`,
    };
  }
  if ((hasTimeZone || hasRole || hasName) && (!companyId || !teamId)) {
    return {
      success: false,
      message:
        'companyId and teamId are required for name, timeZone, or role update',
    };
  }
  try {
    const needsRoster = hasTimeZone || hasRole || hasName;
    const needsDesk = hasDeskId;
    const memberRef = database.collection('membersDatabase').doc(userId);

    let previousCompanyRole = null;
    if (needsRoster && hasRole) {
      const preSnap = await memberRef
        .collection('companyList')
        .doc(companyId)
        .get();
      if (preSnap.exists) {
        previousCompanyRole = preSnap.data().role ?? null;
      }
    }

    let teamIdsForUserInCompany = [];
    if (needsRoster && hasName) {
      const teamListSnap = await memberRef
        .collection('companyList')
        .doc(companyId)
        .collection('teamList')
        .get();
      teamIdsForUserInCompany = teamListSnap.docs
        .filter((d) => (d.data() || {}).deleted !== true)
        .map((d) => d.id);
    }

    const memberPatch = {};
    if (hasTimeZone) {
      memberPatch.timeZone = timeZone.trim();
    }
    if (hasName) {
      memberPatch.accountFullName = String(name).trim();
    }

    const trimmedDisplayName = hasName ? String(name).trim() : '';
    const teamIdsForMemberDetails = needsRoster
      ? (() => {
          if (hasName) {
            const ids = new Set(teamIdsForUserInCompany);
            ids.add(teamId);
            return [...ids];
          }
          if (hasRole) {
            return [teamId];
          }
          return [];
        })()
      : [];

    if (needsDesk || needsRoster) {
      console.log('[updateEmployeeDetails] atomic transaction', {
        needsDesk,
        needsRoster,
        hasTimeZone,
        hasRole,
        hasName,
        teamId,
        companyId,
      });

      const teamDb = database.collection('teamDatabase');

      await database.runTransaction(async (transaction) => {
        let encryptedLicenseKeyToAssign = null;

        if (needsDesk) {
          const newDeskId = String(deskId).trim();
          const licenseRef = database.collection('licenseDatabase');
          const licenseQuery = licenseRef
            .where('deskId', '==', newDeskId)
            .limit(1);
          const licenseSnap = await transaction.get(licenseQuery);
          if (licenseSnap.empty) {
            throw new Error('License not found for this deskId');
          }
          encryptedLicenseKeyToAssign = licenseSnap.docs[0].data().value;
          if (!encryptedLicenseKeyToAssign) {
            throw new Error('License data incomplete');
          }
          const membersQuery = database
            .collection('membersDatabase')
            .where('encryptedLicenseKey', '==', encryptedLicenseKeyToAssign);
          const membersSnap = await transaction.get(membersQuery);
          membersSnap.docs.forEach((doc) => {
            if (doc.id !== userId) {
              transaction.update(doc.ref, { encryptedLicenseKey: null });
            }
          });
        }

        await transaction.get(memberRef);

        const teamReadContext = new Map();
        if (needsRoster && teamIdsForMemberDetails.length > 0) {
          for (const tid of teamIdsForMemberDetails) {
            const tRef = teamDb.doc(tid);
            const teamSnap = await transaction.get(tRef);
            const shardSnapshots = await readShardSnapshotsInTransaction(
              transaction,
              tRef,
              teamSnap.data() || {}
            );
            teamReadContext.set(tid, { teamSnap, shardSnapshots });
          }
        }

        const memberWrite = { ...memberPatch };
        if (needsDesk) {
          memberWrite.encryptedLicenseKey = encryptedLicenseKeyToAssign;
        }
        if (Object.keys(memberWrite).length > 0) {
          transaction.update(memberRef, memberWrite);
        }

        if (needsRoster && hasRole) {
          const companyListRef = memberRef
            .collection('companyList')
            .doc(companyId);
          const teamListRef = companyListRef.collection('teamList').doc(teamId);
          transaction.set(companyListRef, { role }, { merge: true });
          transaction.set(teamListRef, { Role: role }, { merge: true });
        }

        if (needsRoster) {
          for (const tid of teamIdsForMemberDetails) {
            const { teamSnap, shardSnapshots } = teamReadContext.get(tid);
            const location = resolveMemberRosterLocation(
              teamSnap.data() || {},
              shardSnapshots,
              userId
            );
            if (location.type === 'none') {
              throw new Error(
                `Member is not on this team's roster (team ${tid}). Try refreshing the page.`
              );
            }

            /** @type {Partial<{ userName: string, userType: string }>} */
            const rosterFields = {};
            if (hasName) rosterFields.userName = trimmedDisplayName;
            if (hasRole && tid === teamId) rosterFields.userType = role;

            const hasRosterUpdate =
              rosterFields.userName !== undefined ||
              rosterFields.userType !== undefined;
            if (!hasRosterUpdate) continue;

            const { ref, data } = buildRosterPatchWrite(
              teamDb.doc(tid),
              location,
              userId,
              rosterFields
            );
            transaction.update(ref, data);
          }
        }
      });

      console.log('[updateEmployeeDetails] transaction completed');

      if (needsRoster && hasRole && role === 'Admin') {
        const addResult = await addAdminToAllCompanyTeams(userId, companyId);
        if (!addResult.success) {
          console.warn(
            '[updateEmployeeDetails] addAdminToAllCompanyTeams failed',
            addResult.message
          );
          return { success: false, message: addResult.message };
        }
      }

      if (
        needsRoster &&
        hasRole &&
        previousCompanyRole === 'Admin' &&
        (role === 'Member' || role === 'Manager')
      ) {
        const demoteResult = await demoteCompanyAdminToRole(
          userId,
          companyId,
          role
        );
        if (!demoteResult.success) {
          console.warn(
            '[updateEmployeeDetails] demoteCompanyAdminToRole failed',
            demoteResult.message
          );
          return { success: false, message: demoteResult.message };
        }
      }
    }
    console.log('[updateEmployeeDetails] success');
    return { success: true };
  } catch (e) {
    console.error('[updateEmployeeDetails] error', e, 'payload', payload);
    return {
      success: false,
      message: e.message || 'Failed to update employee details',
    };
  }
}

/**
 * Soft-remove user from team and revoke license: set deleted/deletedAt on team and teamList,
 * clear encryptedLicenseKey. Used from Employee List.
 * When removing from the user's active team (in the active company), we must ensure the user
 * still has at least one team and we set a new activeTeamId.
 * @param {Object} payload - { userId, companyId, teamId, newActiveTeamId?: string }
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function removeUsers(payload) {
  const { userId, companyId, teamId, newActiveTeamId } = payload;
  if (!userId || !companyId || !teamId) {
    return {
      success: false,
      message: 'userId, companyId and teamId are required',
    };
  }
  try {
    const memberRef = database.collection('membersDatabase').doc(userId);
    const [memberSnap, teamListSnap] = await Promise.all([
      memberRef.get(),
      memberRef
        .collection('companyList')
        .doc(companyId)
        .collection('teamList')
        .get(),
    ]);

    const memberData = memberSnap.exists ? memberSnap.data() || {} : {};
    const activeCompanyId = memberData.activeCompanyId || null;
    const activeTeamId = memberData.activeTeamId || null;

    const teamsInCompany = [];
    teamListSnap.forEach((doc) => {
      const d = doc.data() || {};
      if (d.deleted === true) return;
      teamsInCompany.push(doc.id);
    });

    // Rule (1): user must remain in at least one team (in this company)
    if (!teamsInCompany.includes(teamId)) {
      return {
        success: false,
        message: 'User is not an active member of this team.',
      };
    }
    if (teamsInCompany.length <= 1) {
      return {
        success: false,
        message:
          'This user is only in one team. Add them to another team first, then delete.',
      };
    }

    const otherTeamIds = teamsInCompany.filter((t) => t !== teamId);
    const isDeletingFromActiveTeam =
      activeCompanyId === companyId && activeTeamId === teamId;

    let resolvedNewActiveTeamId = null;
    if (isDeletingFromActiveTeam) {
      if (newActiveTeamId) {
        if (!otherTeamIds.includes(newActiveTeamId)) {
          return {
            success: false,
            message:
              'Invalid newActiveTeamId. Please select a valid team to set active.',
          };
        }
        resolvedNewActiveTeamId = newActiveTeamId;
      } else if (otherTeamIds.length === 1) {
        // Rule (2): exactly two teams -> auto-select the remaining team as active
        resolvedNewActiveTeamId = otherTeamIds[0];
      } else {
        // Rule (5): multiple teams -> caller must choose
        return {
          success: false,
          message:
            'User is being removed from the active team. Please choose which team to make active first.',
        };
      }
    }

    const teamRef = database.collection('teamDatabase').doc(teamId);

    // Fetch team doc to locate which shard(s) contain this user
    const teamSnap = await teamRef.get();
    const shardIds = Array.isArray(teamSnap.data()?.memberDetailsShardIds)
      ? teamSnap.data().memberDetailsShardIds
      : [];
    const shardsRef = teamRef.collection('memberDetailsShards');
    const shardSnaps = shardIds.length
      ? await Promise.all(shardIds.map((id) => shardsRef.doc(id).get()))
      : [];

    const batch = database.batch();

    // Mark deleted in legacy memberDetails map
    batch.update(teamRef, {
      [`memberDetails.${userId}.deleted`]: true,
      [`memberDetails.${userId}.deletedAt`]:
        firebase.firestore.FieldValue.serverTimestamp(),
    });

    // Mark deleted in whichever shard contains this user
    for (const shardSnap of shardSnaps) {
      if (!shardSnap.exists) continue;
      if (userId in (shardSnap.data()?.members || {})) {
        batch.update(shardSnap.ref, {
          [`members.${userId}.deleted`]: true,
          [`members.${userId}.deletedAt`]:
            firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
    }

    const teamListRef = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('companyList')
      .doc(companyId)
      .collection('teamList')
      .doc(teamId);
    batch.set(
      teamListRef,
      {
        deleted: true,
        deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true }
    );
    const memberUpdate = {};
    if (isDeletingFromActiveTeam && resolvedNewActiveTeamId) {
      memberUpdate.activeTeamId = resolvedNewActiveTeamId;
    }
    if (Object.keys(memberUpdate).length) {
      batch.update(memberRef, memberUpdate);
    }
    await batch.commit();
    return { success: true };
  } catch (e) {
    console.error('removeUsers error:', e);
    return {
      success: false,
      message: e.message || 'Failed to remove user from team',
    };
  }
}

/**
 * Soft-remove user from company: marks companyList and all teamList/team memberDetails
 * for that company as deleted, clears license, updates active company/team if needed,
 * and removes user from company admins. Separate from removeUsers (remove from team).
 * @param {Object} payload - { userId, companyId }
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function removeUserFromCompany(payload) {
  const { userId, companyId } = payload;
  if (!userId || !companyId) {
    return {
      success: false,
      message: 'userId and companyId are required',
    };
  }
  try {
    const memberRef = database.collection('membersDatabase').doc(userId);
    const companyListRef = memberRef.collection('companyList').doc(companyId);

    const [memberSnap, companyListSnap] = await Promise.all([
      memberRef.get(),
      companyListRef.get(),
    ]);

    if (!memberSnap.exists) {
      return { success: false, message: 'User not found.' };
    }
    if (!companyListSnap.exists) {
      return { success: false, message: 'User is not in this company.' };
    }

    const companyListData = companyListSnap.data() || {};
    if (companyListData.deleted === true) {
      return {
        success: false,
        message: 'User is already removed from this company.',
      };
    }

    const memberData = memberSnap.data() || {};
    const activeCompanyId = memberData.activeCompanyId || null;
    const activeTeamId = memberData.activeTeamId || null;

    const teamListSnap = await companyListRef.collection('teamList').get();
    const teamIds = [];
    teamListSnap.forEach((doc) => {
      const d = doc.data() || {};
      if (d.deleted === true) return;
      teamIds.push(doc.id);
    });

    let newActiveCompanyId = null;
    let newActiveTeamId = null;
    if (activeCompanyId === companyId) {
      const allCompanyListSnap = await memberRef
        .collection('companyList')
        .get();
      for (const doc of allCompanyListSnap.docs) {
        if (doc.id === companyId) continue;
        const data = doc.data() || {};
        if (data.deleted === true) continue;
        newActiveCompanyId = doc.id;
        const teamSnap = await doc.ref.collection('teamList').get();
        const firstTeam = teamSnap.docs.find(
          (t) => (t.data() || {}).deleted !== true
        );
        if (firstTeam) newActiveTeamId = firstTeam.id;
        break;
      }
    }

    const batch = database.batch();

    batch.set(
      companyListRef,
      {
        deleted: true,
        deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true }
    );

    for (const teamId of teamIds) {
      const teamRef = database.collection('teamDatabase').doc(teamId);
      batch.update(teamRef, {
        [`memberDetails.${userId}.deleted`]: true,
        [`memberDetails.${userId}.deletedAt`]:
          firebase.firestore.FieldValue.serverTimestamp(),
      });
      const teamListRef = companyListRef.collection('teamList').doc(teamId);
      batch.set(
        teamListRef,
        {
          deleted: true,
          deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );
    }

    const memberUpdate = {
      encryptedLicenseKey: null,
    };
    if (activeCompanyId === companyId) {
      memberUpdate.activeCompanyId = newActiveCompanyId;
      memberUpdate.activeTeamId = newActiveTeamId;
    }
    batch.update(memberRef, memberUpdate);

    const companyRef = database.collection('companyDatabase').doc(companyId);
    const companySnap = await companyRef.get();
    if (companySnap.exists) {
      const admins = (companySnap.data() || {}).admins || [];
      if (admins.includes(userId)) {
        batch.update(companyRef, {
          admins: firebase.firestore.FieldValue.arrayRemove(userId),
        });
      }
    }
    const removeSeatResponse = await httpClient.post(
      '/decreaseSubscriptionQuantity',
      {
        companyId,
        quantityToRemove: 1,
      }
    );
    if (removeSeatResponse.data.success) {
      await batch.commit();
      return { success: true };
    }
    return {
      success: false,
      message:
        removeSeatResponse.data.message || 'Failed to remove user from company',
    };
  } catch (e) {
    console.error('removeUserFromCompany error:', e);
    return {
      success: false,
      message: e.message || 'Failed to remove user from company',
    };
  }
}

export default {
  getAllTeamMembersData,
  createTeamWithMembers,
  editTeamWithMembers,
  updateMemberDetails,
  deleteTeam,
  deleteMember,
  updateEmployeeDetails,
  removeUsers,
  removeUserFromCompany,
};
